import { ref } from 'vue'
import { getAuthToken, API_BASE_URL } from '@/stores/appConfig'

// Cloudflare Images direct-upload flow (see CLAUDE.md "Image Media Library"),
// extracted from BuilderView.vue's original inline implementation so
// PhysicalMarketingView.vue's ImagePickerModal usage doesn't need to
// duplicate the direct-upload + completion-retry logic. BuilderView.vue's own
// copy is left as-is rather than migrated to this composable — it already
// works, and refactoring it isn't needed for this feature.
export function useTenantImages() {
  const images = ref([])
  const loading = ref(false)
  const uploading = ref(false)
  const uploadError = ref('')
  const deleting = ref(null)

  function addImage(image) {
    if (!image?.url) return
    const existingIndex = images.value.findIndex((existing) =>
      existing.cloudflare_image_id
        ? existing.cloudflare_image_id === image.cloudflare_image_id
        : existing.url === image.url
    )
    if (existingIndex >= 0) {
      images.value.splice(existingIndex, 1, image)
      return
    }
    images.value.unshift(image)
  }

  function cloudflareErrorMessage(data, fallback) {
    return data?.errors?.map((error) => error.message).join('; ') || data?.error || fallback
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async function completeUpload({ token, companyId, cloudflareImageId, filename }) {
    let lastError = null

    for (let attempt = 0; attempt < 4; attempt += 1) {
      const response = await fetch(`${API_BASE_URL}/media/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_id: companyId, cloudflare_image_id: cloudflareImageId, filename }),
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.image?.url) {
        return data.image
      }

      lastError = new Error(data.error || 'Unable to save uploaded image.')

      if (response.status !== 409 || attempt === 3) {
        throw lastError
      }

      await wait(700)
    }

    throw lastError || new Error('Unable to save uploaded image.')
  }

  async function load(companyId) {
    if (!companyId) return
    loading.value = true
    uploadError.value = ''

    try {
      const token = await getAuthToken()
      const response = await fetch(`${API_BASE_URL}/media?company_id=${companyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || 'Unable to load images.')
      images.value = data.images || data.media || []
    } catch (error) {
      console.error('useTenantImages load error:', error)
      uploadError.value = error.message || 'Unable to load images.'
    } finally {
      loading.value = false
    }
  }

  async function upload(companyId, file) {
    if (!companyId || !file) {
      uploadError.value = 'Company is still loading. Try again in a moment.'
      return null
    }

    uploading.value = true
    uploadError.value = ''

    try {
      const token = await getAuthToken()
      if (!token) throw new Error('You need to be signed in to upload images.')

      const directResponse = await fetch(`${API_BASE_URL}/media/direct-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_id: companyId, filename: file.name }),
      })
      const directData = await directResponse.json().catch(() => ({}))
      const cloudflareImageId = directData.image?.cloudflare_image_id

      if (!directResponse.ok || !directData.uploadURL || !cloudflareImageId) {
        throw new Error(directData.error || 'Unable to prepare image upload.')
      }

      const uploadForm = new FormData()
      uploadForm.append('file', file)

      const uploadResponse = await fetch(directData.uploadURL, { method: 'POST', body: uploadForm })
      const uploadData = await uploadResponse.json().catch(() => ({}))

      if (!uploadResponse.ok || uploadData.success === false) {
        throw new Error(cloudflareErrorMessage(uploadData, 'Cloudflare upload failed.'))
      }

      const uploadedImage = await completeUpload({ token, companyId, cloudflareImageId, filename: file.name })
      addImage(uploadedImage)
      return uploadedImage
    } catch (error) {
      console.error('useTenantImages upload error:', error)
      uploadError.value = error.message || 'Unable to upload image.'
      return null
    } finally {
      uploading.value = false
    }
  }

  async function remove(image) {
    const mediaId = image?.id
    if (!mediaId) return

    deleting.value = mediaId
    try {
      const token = await getAuthToken()
      const response = await fetch(`${API_BASE_URL}/media/${mediaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Delete failed.')
      }
      images.value = images.value.filter((img) => img.id !== mediaId)
    } catch (error) {
      console.error('useTenantImages remove error:', error)
      uploadError.value = error.message || 'Unable to delete image.'
    } finally {
      deleting.value = null
    }
  }

  return { images, loading, uploading, uploadError, deleting, load, upload, remove }
}
